/**
 * One retained UI element. Mutated only through {@link UIDocument} (which owns dirty tracking)
 * and the Vue host ops. Layout geometry lands here after each document update.
 */
import type { ILayoutNode } from '../layout/ILayoutEngine';
import type { AnimationDecl, EasingName, Keyframes, PseudoState, StyleDecls } from '../types';

export type UIElementType = 'view' | 'text' | 'image' | 'button';
export type UINodeKind = 'element' | 'rawtext' | 'comment' | 'root';

/** One in-flight transition (UI-18): interpolate `style[key]` from→to over the doc clock. */
export interface Tween {
  from: unknown;
  to: unknown;
  /** doc clock (seconds) at which the tween was started; delay/duration are seconds. */
  start: number;
  delaySec: number;
  durSec: number;
  easing: EasingName;
}

/** One running `animation` (UI-18b): a keyframes track sampled over the doc clock. */
export interface AnimationInstance {
  decl: AnimationDecl;
  track: Keyframes;
  /** doc clock (seconds) at which this instance began playing. */
  start: number;
}

export const UI_ELEMENT_TYPES: ReadonlySet<string> = new Set(['view', 'text', 'image', 'button']);

let nextNodeId = 1;

export class UINode {
  readonly id = nextNodeId++;
  readonly kind: UINodeKind;
  /** For kind 'element'. */
  readonly elementType: UIElementType | null;
  /** Layout node — 'element'/'root' only; null for rawtext/comment. */
  readonly layout: ILayoutNode | null;

  parent: UINode | null = null;
  readonly children: UINode[] = [];

  // authored inputs (host ops write, document resolves)
  classes: string[] = [];
  inline: StyleDecls | null = null;
  attrs: Record<string, unknown> = {};
  /** Event handlers patched by Vue ('click' → fn | fn[]). Input system invokes these. */
  readonly handlers = new Map<string, unknown>();
  readonly states = new Set<PseudoState>();

  /** Resolved own cascade (no inheritance — see UIDocument.inherited). While transitions run this
   *  holds the CURRENT interpolated values (the target is captured in each tween's `to`). */
  style: StyleDecls = {};

  /** Active transitions keyed by style prop (UI-18); lazily created, tracked in UIDocument.animating. */
  tweens: Map<keyof StyleDecls, Tween> | null = null;

  /** Running `animation`s (UI-18b); null when none. Tracked in UIDocument.animating. */
  animations: AnimationInstance[] | null = null;
  /** Resolved cascade WITHOUT animation overrides — the base layer animations sample over. */
  animBase: StyleDecls | null = null;

  /** rawtext content, or an element's direct text set via setElementText. */
  text = '';

  /** Absolute layout rect in LOGICAL units (post-update; scroll offsets already applied). */
  layoutX = 0;
  layoutY = 0;
  layoutW = 0;
  layoutH = 0;

  /** `overflow: scroll` state (logical px; clamped by UIDocument against the content extent). */
  scrollX = 0;
  scrollY = 0;
  /** Content extent of a scroll container (max child right/bottom edge; post-update). */
  contentW = 0;
  contentH = 0;

  constructor(kind: UINodeKind, elementType: UIElementType | null, layout: ILayoutNode | null) {
    this.kind = kind;
    this.elementType = elementType;
    this.layout = layout;
  }

  get isElement(): boolean {
    return this.kind === 'element' || this.kind === 'root';
  }

  /** Aggregated text content of a 'text' element: direct text + descendant rawtext. */
  textContent(): string {
    let out = this.text;
    for (const c of this.children) {
      if (c.kind === 'rawtext') out += c.text;
      else if (c.kind === 'element') out += c.textContent();
    }
    return out;
  }

  /** Nearest ancestor (inclusive) that is a 'text' element. */
  closestTextElement(): UINode | null {
    let n: UINode | null = this;
    while (n) {
      if (n.elementType === 'text') return n;
      n = n.parent;
    }
    return null;
  }

  nextSibling(): UINode | null {
    const p = this.parent;
    if (!p) return null;
    const i = p.children.indexOf(this);
    return p.children[i + 1] ?? null;
  }

  /** True when attached under a document root. */
  isConnected(): boolean {
    let n: UINode | null = this;
    while (n.parent) n = n.parent;
    return n.kind === 'root';
  }
}
